import React, { useState, useEffect } from 'react';
import { Box, Container, Grid, Typography, IconButton, Button, useTheme, Chip } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Link } from 'react-router-dom';
import { profile } from '../../data/profile';
import { playClickSound, playChimeSound } from '../../utils/soundEffects';

const Footer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const istString = now.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      setCurrentTime(istString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCopyEmail = () => {
    playChimeSound();
    navigator.clipboard.writeText(profile.email || 'gk.goyal.0.03@gmail.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const scrollToTop = () => {
    playClickSound();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getSocialIcon = (name) => {
    switch (name) {
      case 'GitHub':
        return <GitHubIcon sx={{ fontSize: 18 }} />;
      case 'LinkedIn':
        return <LinkedInIcon sx={{ fontSize: 18 }} />;
      case 'Instagram':
        return <InstagramIcon sx={{ fontSize: 18 }} />;
      case 'Twitter / X':
        return <TwitterIcon sx={{ fontSize: 18 }} />;
      case 'Codeforces':
        return <CodeIcon sx={{ fontSize: 18 }} />;
      case 'Email':
        return <EmailIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const primaryLinks = [
    { label: 'About the Person', path: '/about', code: '01' },
    { label: 'Selected Work & Systems', path: '/work', code: '02' },
    { label: 'Writing & Manuscript', path: '/writing', code: '03' },
    { label: 'Personal Library', path: '/reading', code: '04' },
  ];

  const secondaryLinks = [
    { label: 'Structured Learning', path: '/learning', code: '05' },
    { label: 'Evolution & Journey', path: '/journey', code: '06' },
    { label: 'Curriculum Vitae', path: '/resume', code: '07' },
    { label: 'Contact Coordinates', path: '/contact', code: '08' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        position: 'relative',
        background: isDark
          ? 'linear-gradient(180deg, #04140d 0%, #020d08 100%)'
          : 'linear-gradient(180deg, #edf7f1 0%, #dcf0e4 100%)',
        borderTop: isDark
          ? '1px solid rgba(16, 185, 129, 0.3)'
          : '1px solid rgba(5, 150, 105, 0.35)',
        boxShadow: isDark
          ? '0 -20px 60px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(52, 211, 153, 0.2)'
          : '0 -10px 40px rgba(5, 150, 105, 0.08)',
        py: { xs: 4, md: 5 },
        color: isDark ? '#e6f4ea' : '#143826',
        overflow: 'hidden',
      }}
    >
      {/* Top Ambient Glow Ribbon */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '75%',
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, #10b981 30%, #34d399 50%, #10b981 70%, transparent 100%)',
          filter: 'blur(1px)',
        }}
      />

      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: '100%', sm: '100%', md: '1440px', lg: '1560px', xl: '1680px' },
          px: { xs: 2.5, sm: 3.5, md: 4.5, lg: 5.5 },
        }}
      >
        {/* Top Operational Status Ribbon */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            pb: 2.5,
            mb: 3,
            borderBottom: isDark ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid rgba(5, 150, 105, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.6,
                py: 0.5,
                borderRadius: '9999px',
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(5, 150, 105, 0.12)',
                border: isDark ? '1px solid rgba(52, 211, 153, 0.35)' : '1px solid rgba(5, 150, 105, 0.3)',
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  boxShadow: '0 0 10px #10b981',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: isDark ? '#34d399' : '#065f46',
                  letterSpacing: '0.08em',
                }}
              >
                OPERATIONAL // ASIA-SOUTH-1 (IST)
              </Typography>
            </Box>

            {currentTime && (
              <Chip
                label={`IST: ${currentTime}`}
                size="small"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: isDark ? '#a7f3d0' : '#047857',
                  backgroundColor: isDark ? 'rgba(52, 211, 153, 0.08)' : 'rgba(5, 150, 105, 0.08)',
                  border: isDark ? '1px solid rgba(52, 211, 153, 0.2)' : '1px solid rgba(5, 150, 105, 0.2)',
                  height: 24,
                }}
              />
            )}
          </Box>

          <Button
            onClick={scrollToTop}
            startIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.74rem',
              fontWeight: 700,
              color: isDark ? '#34d399' : '#047857',
              backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(5, 150, 105, 0.1)',
              border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid rgba(5, 150, 105, 0.3)',
              borderRadius: '9999px',
              px: 2,
              py: 0.5,
              textTransform: 'none',
              letterSpacing: '0.04em',
              transition: 'all 200ms ease',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(5, 150, 105, 0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)',
              },
            }}
          >
            Back to Top
          </Button>
        </Box>

        {/* 4-Column Structured Architecture Grid */}
        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="space-between">
          {/* Column 1: Identity, Colophon & Social Coordinates */}
          <Grid item xs={12} md={4.5}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                  fontWeight: 800,
                  fontSize: { xs: '1.4rem', md: '1.65rem' },
                  letterSpacing: '-0.02em',
                  background: isDark
                    ? 'linear-gradient(135deg, #ffffff 30%, #34d399 75%, #10b981 100%)'
                    : 'linear-gradient(135deg, #064e3b 0%, #047857 70%, #059669 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                  mb: 0.4,
                }}
              >
                Girish Kumar Goyal
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  color: isDark ? '#34d399' : '#059669',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  mb: 1.2,
                }}
              >
                Systems Engineer · Candidate Master (2013) · Author
              </Typography>
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: isDark ? '#94a3b8' : '#334155',
                lineHeight: 1.65,
                fontSize: '0.86rem',
                maxWidth: 420,
                mb: 2.2,
              }}
            >
              Solving algorithmic proofs on Codeforces, building independent software systems, and authoring <em>"Biology through Curiosity : Questions that became Knowledge"</em>.
            </Typography>

            {/* Social Channels with Glowing Emerald Micro-interactions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {profile.socialLinks.map((social) => (
                <IconButton
                  key={social.name}
                  component="a"
                  href={social.url}
                  target={social.url.startsWith('mailto:') ? '_self' : '_blank'}
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  size="small"
                  onClick={playClickSound}
                  sx={{
                    color: isDark ? '#a7f3d0' : '#065f46',
                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.08)' : 'rgba(5, 150, 105, 0.08)',
                    border: isDark ? '1px solid rgba(52, 211, 153, 0.25)' : '1px solid rgba(5, 150, 105, 0.25)',
                    borderRadius: '10px',
                    p: 1,
                    transition: 'all 200ms ease',
                    '&:hover': {
                      color: '#ffffff',
                      backgroundColor: '#10b981',
                      borderColor: '#34d399',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                    },
                  }}
                >
                  {getSocialIcon(social.name)}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Column 2: Core Archives (01-04) */}
          <Grid item xs={6} sm={6} md={2.3}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                color: isDark ? '#34d399' : '#047857',
                mb: 2.2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <Box component="span" sx={{ color: isDark ? '#6ee7b7' : '#059669' }}>{'//'}</Box> CORE SYSTEMS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {primaryLinks.map((link) => (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  onClick={playClickSound}
                  sx={{
                    color: isDark ? '#cbd5e1' : '#334155',
                    textDecoration: 'none',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    transition: 'all 160ms ease',
                    '&:hover': {
                      color: isDark ? '#34d399' : '#059669',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: isDark ? '#10b981' : '#059669',
                      opacity: 0.8,
                    }}
                  >
                    {link.code}
                  </Box>
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Column 3: Exploration (05-08) */}
          <Grid item xs={6} sm={6} md={2.3}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                color: isDark ? '#34d399' : '#047857',
                mb: 2.2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <Box component="span" sx={{ color: isDark ? '#6ee7b7' : '#059669' }}>{'//'}</Box> EXPLORATION
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {secondaryLinks.map((link) => (
                <Typography
                  key={link.path}
                  component={Link}
                  to={link.path}
                  onClick={playClickSound}
                  sx={{
                    color: isDark ? '#cbd5e1' : '#334155',
                    textDecoration: 'none',
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    transition: 'all 160ms ease',
                    '&:hover': {
                      color: isDark ? '#34d399' : '#059669',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: isDark ? '#10b981' : '#059669',
                      opacity: 0.8,
                    }}
                  >
                    {link.code}
                  </Box>
                  {link.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Column 4: Quick Direct Access & Telemetry */}
          <Grid item xs={12} sm={12} md={2.8}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                color: isDark ? '#34d399' : '#047857',
                mb: 2.2,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
              }}
            >
              <Box component="span" sx={{ color: isDark ? '#6ee7b7' : '#059669' }}>{'//'}</Box> DIRECT CHANNELS
            </Typography>

            {/* 1-Click Copy Email Card */}
            <Box
              onClick={handleCopyEmail}
              sx={{
                p: 1.5,
                mb: 1.8,
                borderRadius: '10px',
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.06)' : 'rgba(5, 150, 105, 0.08)',
                border: isDark ? '1px solid rgba(52, 211, 153, 0.25)' : '1px solid rgba(5, 150, 105, 0.25)',
                cursor: 'pointer',
                transition: 'all 200ms ease',
                '&:hover': {
                  borderColor: '#10b981',
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : 'rgba(5, 150, 105, 0.14)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', color: isDark ? '#34d399' : '#059669', fontWeight: 700 }}>
                  DIRECT INBOX
                </Typography>
                {copied ? <CheckIcon sx={{ fontSize: 14, color: '#34d399' }} /> : <ContentCopyIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
              </Box>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 600 }}>
                {profile.email || 'gk.goyal.0.03@gmail.com'}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: copied ? '#34d399' : '#94a3b8', mt: 0.3 }}>
                {copied ? '✓ Copied to clipboard' : 'Click to copy email'}
              </Typography>
            </Box>

            {/* Codeforces Live Telemetry Pill */}
            <Box
              component="a"
              href="https://codeforces.com/profile/epsilon_xd"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                textDecoration: 'none',
                p: 1.2,
                borderRadius: '8px',
                border: isDark ? '1px solid rgba(56, 189, 248, 0.25)' : '1px solid rgba(2, 132, 199, 0.25)',
                backgroundColor: isDark ? 'rgba(56, 189, 248, 0.05)' : 'rgba(2, 132, 199, 0.06)',
                transition: 'all 200ms ease',
                '&:hover': {
                  borderColor: '#38bdf8',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', color: '#38bdf8', fontWeight: 700 }}>
                CODEFORCES CANDIDATE MASTER
              </Typography>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.74rem', color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 700, mt: 0.2 }}>
                epsilon_xd · 2013 Max Rating ↗
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Colophon */}
        <Box
          sx={{
            mt: 3.5,
            pt: 2.5,
            borderTop: isDark ? '1px solid rgba(16, 185, 129, 0.15)' : '1px solid rgba(5, 150, 105, 0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.74rem',
              color: isDark ? '#94a3b8' : '#475569',
              textAlign: 'center',
            }}
          >
            © {new Date().getFullYear()} <Box component="span" sx={{ color: isDark ? '#34d399' : '#059669', fontWeight: 700 }}>Girish Kumar Goyal</Box> · India · All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
